import { useCallback, useMemo } from "react";
import { fetchCompanies } from "@/api/fetchCompanies/fetchCompaniesApi";
import {
  BaseEntity,
  FetchEntitiesFn,
} from "@/components/EntityPickerModal/EntityPickerModal";
import { FilterConfig } from "@/components/ChipFilterGroup/ChipFilterGroup";
import {
  OrderListFilters as OrderFilters,
  useOrderListPage,
} from "./useOrderListPage";
import { ProductStoreOrderStatus } from "../api/fetchProductStoreOrders/types";
import {
  ORDER_STATUS_STYLES,
  ORDER_STATUS_VALUES,
} from "../utils/orderStatusStyles";

const SEARCH_PLACEHOLDER = "Search by name, email, company, or confirmation #…";

function stringToDate(value: string | undefined): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function dateToString(value: Date | null): string | undefined {
  if (!value) return undefined;
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

interface UseOrderListFiltersInput {
  filters: OrderFilters;
  onFiltersChange: ReturnType<typeof useOrderListPage>["handleFiltersChange"];
}

export function useOrderListFilters({
  filters,
  onFiltersChange,
}: UseOrderListFiltersInput) {
  const fetchStatusEntities: FetchEntitiesFn<BaseEntity> = useCallback(
    async ({ searchTerm }) => {
      const term = searchTerm.trim().toLowerCase();
      const all: BaseEntity[] = ORDER_STATUS_VALUES.map((value) => ({
        id: value,
        name: ORDER_STATUS_STYLES[value].label,
      }));
      const data = term
        ? all.filter((entity) => entity.name.toLowerCase().includes(term))
        : all;
      return { data, totalCount: data.length };
    },
    [],
  );

  const fetchCompanyEntities: FetchEntitiesFn<BaseEntity> = useCallback(
    async ({ searchTerm, page, pageSize }) => {
      const response = await fetchCompanies({
        searchTerm: searchTerm || undefined,
        page,
        pageSize,
      });
      const data: BaseEntity[] = (response.data?.companies || []).map(
        (company) => ({
          id: company.uuid,
          name: company.companyName,
        }),
      );
      return { data, totalCount: response.meta?.totalCount || data.length };
    },
    [],
  );

  const filterConfigs: FilterConfig<BaseEntity>[] = useMemo(
    () => [
      {
        key: "status",
        type: "multi",
        label: "Status",
        color: "blue",
        entityNameSingular: "Status",
        entityNamePlural: "Statuses",
        fetchEntities: fetchStatusEntities,
      },
      {
        key: "company",
        type: "single",
        label: "Company",
        color: "green",
        entityNameSingular: "Company",
        entityNamePlural: "Companies",
        fetchEntities: fetchCompanyEntities,
      },
    ],
    [fetchStatusEntities, fetchCompanyEntities],
  );

  const filterValues = useMemo(
    () => ({
      status: filters.status.map((value) => ({
        id: value,
        name: ORDER_STATUS_STYLES[value].label,
      })),
      company:
        filters.companyUuid && filters.companyName
          ? { id: filters.companyUuid, name: filters.companyName }
          : null,
    }),
    [filters.status, filters.companyUuid, filters.companyName],
  );

  const handleChipChange = useCallback(
    (key: string, value: BaseEntity[] | BaseEntity | null) => {
      if (key === "status") {
        const next = (Array.isArray(value) ? value : []).map(
          (entity) => entity.id as ProductStoreOrderStatus,
        );
        onFiltersChange({ status: next });
      } else if (key === "company") {
        if (value && !Array.isArray(value)) {
          onFiltersChange({
            companyUuid: String(value.id),
            companyName: value.name,
          });
        } else {
          onFiltersChange({
            companyUuid: undefined,
            companyName: undefined,
          });
        }
      }
    },
    [onFiltersChange],
  );

  const handleClearAll = useCallback(() => {
    onFiltersChange({
      status: [],
      companyUuid: undefined,
      companyName: undefined,
    });
  }, [onFiltersChange]);

  const onSearchChange = useCallback(
    (value: string) => onFiltersChange({ searchTerm: value }),
    [onFiltersChange],
  );

  const onStartDateChange = useCallback(
    (value: Date | null) => onFiltersChange({ startDate: dateToString(value) }),
    [onFiltersChange],
  );

  const onEndDateChange = useCallback(
    (value: Date | null) => onFiltersChange({ endDate: dateToString(value) }),
    [onFiltersChange],
  );

  const hasActiveFilters =
    filters.status.length > 0 || Boolean(filters.companyUuid);

  return {
    filterConfigs,
    filterValues,
    handleChipChange,
    handleClearAll,
    hasActiveFilters,
    searchTerm: filters.searchTerm,
    onSearchChange,
    startDate: stringToDate(filters.startDate),
    onStartDateChange,
    endDate: stringToDate(filters.endDate),
    onEndDateChange,
    searchPlaceholder: SEARCH_PLACEHOLDER,
  };
}
